/**
 * Ticket Type Definitions (Modular Ticket Types)
 *
 * A `TicketTypeDef` is an org-defined, customizable classification for tickets —
 * the same pattern as CRM stages (per-org rows, seeded with sensible defaults,
 * fully editable). Each type bundles:
 *   - vocabulary (name, icon, color)
 *   - a workflow (its own subset/ordering of TICKET CrmStages, scoped by typeId)
 *   - a set of CAPABILITY toggles that turn behaviors on/off (modular)
 *   - visibility scoping
 *
 * The behavior of a ticket is driven by its type's capabilities, NOT hard-coded.
 * Turning a capability OFF only hides the UI — it never deletes data.
 *
 * Pre-configured but customizable: orgs are seeded with a few system types and
 * can install more from the template registry (TICKET_TYPE_TEMPLATES), then
 * rename/recolor/toggle/edit them. Only `key` is immutable (API stability).
 */

import {
  CrmStageConfig,
  CrmStageTypeEnum,
  DEFAULT_TICKET_STAGES,
  TicketStatusTypeEnum,
} from './crm-stage'

/* -------------------------------------------------------------------------- */
/*  Capabilities                                                              */
/* -------------------------------------------------------------------------- */

/**
 * The modular behavior toggles available on a ticket type. The ticket UI and
 * API gate features on these flags (see the `can()` helper in the web app).
 */
export interface TicketCapabilities {
  /** Multiple contacts/entities can co-sign onto one ticket (TicketRequester). */
  allowsMultiRequester: boolean
  /** Ticket can be promoted into a delivery Task. */
  allowsPromoteToTask: boolean
  /** Ticket can be promoted into a Project. */
  allowsPromoteToProject: boolean
  /** Ticket can be submitted via a public Form. */
  allowsPublicSubmit: boolean
  /** Ticket appears on the public roadmap when marked public. */
  allowsPublicView: boolean
  /** Ticket tracks an SLA timer. */
  hasSlaTimer: boolean
  /** Ticket goes through an approval workflow (TicketApprovalHistory). */
  requiresApproval: boolean
  /** Requester aggregation is weighted by deal value / plan tier. */
  weightedAggregation: boolean
  /** New tickets of this type default to restricted visibility. */
  privateByDefault: boolean
}

export type TicketCapabilityKey = keyof TicketCapabilities

/** Safe baseline — everything off except the one near-universal behavior. */
export const DEFAULT_TICKET_CAPABILITIES: TicketCapabilities = {
  allowsMultiRequester: false,
  allowsPromoteToTask: true,
  allowsPromoteToProject: false,
  allowsPublicSubmit: false,
  allowsPublicView: false,
  hasSlaTimer: false,
  requiresApproval: false,
  weightedAggregation: false,
  privateByDefault: false,
}

/** UI metadata for the capability checkboxes on the settings page. */
export const TICKET_CAPABILITY_META: Record<
  TicketCapabilityKey,
  { label: string; description: string }
> = {
  allowsMultiRequester: {
    label: 'Multiple requesters',
    description:
      'Let several contacts or accounts co-sign onto the same ticket, and rank by demand.',
  },
  allowsPromoteToTask: {
    label: 'Promote to task',
    description:
      'Turn a ticket into a delivery task linked back to this ticket.',
  },
  allowsPromoteToProject: {
    label: 'Promote to project',
    description: 'Spin a ticket up into a full project.',
  },
  allowsPublicSubmit: {
    label: 'Public intake',
    description: 'Accept submissions of this type through a public form.',
  },
  allowsPublicView: {
    label: 'Public roadmap',
    description: 'Show tickets of this type on a read-only public roadmap.',
  },
  hasSlaTimer: {
    label: 'SLA timer',
    description: 'Track a response/resolution deadline and flag breaches.',
  },
  requiresApproval: {
    label: 'Requires approval',
    description: 'Route tickets through a submit → approve/reject workflow.',
  },
  weightedAggregation: {
    label: 'Revenue-weighted demand',
    description:
      'Weight requester counts by linked deal value or account plan tier.',
  },
  privateByDefault: {
    label: 'Private by default',
    description:
      'New tickets are visible only to the requester, assignees, and admins.',
  },
}

/* -------------------------------------------------------------------------- */
/*  Visibility                                                                */
/* -------------------------------------------------------------------------- */

export enum TicketTypeVisibilityScope {
  /** Visible to everyone in the org (default). */
  ALL = 'ALL',
  /** Visible only to members holding one of `visibleToRoleKeys`. */
  RESTRICTED = 'RESTRICTED',
}

/* -------------------------------------------------------------------------- */
/*  Workflow stage source                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Where a ticket type draws its workflow stages from (mirrors the task-type
 * `statusSource` concept).
 *
 * - `DEFAULT` — the type uses the org's default TICKET stages (the "Default
 *   stages" card). It carries no workflow of its own; changing the org defaults
 *   changes this type too.
 * - `CUSTOM` — the type has its own stage set (CrmStage rows scoped by
 *   `ticketTypeDefId`), independent of the org defaults.
 *
 * Switching a CUSTOM type back to DEFAULT keeps its custom rows dormant so they
 * can be restored by switching back — nothing is deleted.
 */
export enum TicketTypeStageSource {
  DEFAULT = 'DEFAULT',
  CUSTOM = 'CUSTOM',
}

/* -------------------------------------------------------------------------- */
/*  Type definition records                                                   */
/* -------------------------------------------------------------------------- */

/** Full TicketTypeDef record as stored/returned. */
export interface TicketTypeDef extends TicketCapabilities {
  id: string
  organizationId: string
  key: string
  name: string
  pluralName: string | null
  description: string | null
  icon: string | null
  color: string | null
  isEnabled: boolean
  isSystem: boolean
  isArchived: boolean
  order: number
  defaultStageId: string | null
  stageSource: TicketTypeStageSource
  requiredFields: string[]
  customFieldDefIds: string[]
  defaultAssigneeId: string | null
  defaultPriority: string | null
  slaTargetMinutes: number | null
  // First-response SLA target + behavior config (see ticket-sla.ts
  // TicketSlaConfig). Only meaningful when hasSlaTimer.
  slaFirstResponseMinutes: number | null
  slaConfig: Record<string, unknown>
  // Task→ticket status sync config (see ticket-task-sync.ts
  // TicketTaskSyncConfig). Applies to tickets promoted/split to tasks.
  taskSyncConfig: Record<string, unknown>
  // Task type that tickets of this type become when promoted or split into
  // tasks (see ticket-task-type.ts resolveTicketTaskType). Null falls back to
  // a task type whose key or name matches this ticket type.
  defaultTaskTypeDefId: string | null
  // Post-resolution CSAT survey opt-in for tickets of this type.
  sendCsatSurvey: boolean
  // Auto-assignment at create time (see ticket-sla.ts
  // TicketAssignmentStrategyEnum): NONE | ROUND_ROBIN | LOAD_BALANCED.
  assignmentStrategy: string
  assigneePoolIds: string[]
  assignmentMaxOpenTickets: number | null
  visibilityScope: TicketTypeVisibilityScope
  visibleToRoleKeys: string[]
  createdAt: Date
  updatedAt: Date
}

export interface CreateTicketTypeDef {
  key: string
  name: string
  pluralName?: string | null
  description?: string | null
  icon?: string | null
  color?: string | null
  order?: number
  defaultStageId?: string | null
  stageSource?: TicketTypeStageSource
  requiredFields?: string[]
  customFieldDefIds?: string[]
  defaultAssigneeId?: string | null
  defaultPriority?: string | null
  slaTargetMinutes?: number | null
  slaFirstResponseMinutes?: number | null
  slaConfig?: Record<string, unknown>
  taskSyncConfig?: Record<string, unknown>
  defaultTaskTypeDefId?: string | null
  sendCsatSurvey?: boolean
  assignmentStrategy?: string
  assigneePoolIds?: string[]
  assignmentMaxOpenTickets?: number | null
  visibilityScope?: TicketTypeVisibilityScope
  visibleToRoleKeys?: string[]
  capabilities?: Partial<TicketCapabilities>
}

export interface UpdateTicketTypeDef {
  name?: string
  pluralName?: string | null
  description?: string | null
  icon?: string | null
  color?: string | null
  isEnabled?: boolean
  isArchived?: boolean
  order?: number
  defaultStageId?: string | null
  stageSource?: TicketTypeStageSource
  requiredFields?: string[]
  customFieldDefIds?: string[]
  defaultAssigneeId?: string | null
  defaultPriority?: string | null
  slaTargetMinutes?: number | null
  slaFirstResponseMinutes?: number | null
  slaConfig?: Record<string, unknown>
  taskSyncConfig?: Record<string, unknown>
  defaultTaskTypeDefId?: string | null
  sendCsatSurvey?: boolean
  assignmentStrategy?: string
  assigneePoolIds?: string[]
  assignmentMaxOpenTickets?: number | null
  visibilityScope?: TicketTypeVisibilityScope
  visibleToRoleKeys?: string[]
  capabilities?: Partial<TicketCapabilities>
}

/* -------------------------------------------------------------------------- */
/*  Requesters (multi-requester capability)                                   */
/* -------------------------------------------------------------------------- */

export enum TicketRequesterSource {
  EMAIL = 'EMAIL',
  FORM = 'FORM',
  MANUAL = 'MANUAL',
  MERGED = 'MERGED',
  API = 'API',
  BENNY = 'BENNY',
}

export interface TicketRequester {
  id: string
  organizationId: string
  ticketId: string
  contactId: string | null
  entityId: string | null
  userId: string | null
  email: string | null
  name: string | null
  message: string | null
  source: TicketRequesterSource
  sourceTicketId: string | null
  weight: number | null
  createdAt: Date
}

export interface AddTicketRequester {
  contactId?: string | null
  entityId?: string | null
  userId?: string | null
  email?: string | null
  name?: string | null
  message?: string | null
  source?: TicketRequesterSource
  weight?: number | null
}

/* -------------------------------------------------------------------------- */
/*  Template registry (pre-configured, installable)                          */
/* -------------------------------------------------------------------------- */

export enum TicketTypeBundle {
  PRODUCT_ENG = 'product_eng',
  SUPPORT = 'support',
  SALES = 'sales',
  LEGAL = 'legal',
  HR = 'hr',
  IT = 'it',
  FINANCE = 'finance',
  GENERIC = 'generic',
}

export const TICKET_TYPE_BUNDLE_LABELS: Record<TicketTypeBundle, string> = {
  [TicketTypeBundle.PRODUCT_ENG]: 'Product & engineering',
  [TicketTypeBundle.SUPPORT]: 'Customer support',
  [TicketTypeBundle.SALES]: 'Sales & success',
  [TicketTypeBundle.LEGAL]: 'Legal',
  [TicketTypeBundle.HR]: 'HR & people',
  [TicketTypeBundle.IT]: 'IT & ops',
  [TicketTypeBundle.FINANCE]: 'Finance',
  [TicketTypeBundle.GENERIC]: 'General',
}

/** An installable, pre-configured ticket type. */
export interface TicketTypeTemplate {
  key: string
  name: string
  pluralName: string
  description: string
  icon: string
  color: string
  bundle: TicketTypeBundle
  capabilities: Partial<TicketCapabilities>
  /** The type's workflow. Stage `id`s are scoped to this type. */
  stages: CrmStageConfig[]
  defaultStageId: string
  requiredFields?: string[]
  visibilityScope?: TicketTypeVisibilityScope
  /** Whether this type is installed automatically for brand-new orgs. */
  seededByDefault?: boolean
  /**
   * Whether this type is installed automatically for software/technology orgs
   * only (on top of the universal {@link seededByDefault} set). Product-led
   * types like Feature Request and Bug aren't relevant to most businesses, so
   * they're seeded only when the org identifies as a tech company at creation.
   * Any org can still install them later from the template catalog.
   */
  seededForTech?: boolean
}

/* ----- Reusable stage sets ------------------------------------------------ */

const t = (
  id: string,
  name: string,
  color: string,
  stageTypeId: TicketStatusTypeEnum,
  order: number,
  opts: { isDefault?: boolean; isHidden?: boolean } = {},
): CrmStageConfig => ({
  id,
  name,
  color,
  stageTypeId: stageTypeId as unknown as CrmStageTypeEnum,
  order,
  probability: 0,
  isDefault: opts.isDefault,
  isHidden: opts.isHidden,
})

/** Generic support flow — reuses the canonical TICKET stages. */
const SUPPORT_STAGES: CrmStageConfig[] = DEFAULT_TICKET_STAGES

const FEATURE_REQUEST_STAGES: CrmStageConfig[] = [
  t('submitted', 'Submitted', '#64748B', TicketStatusTypeEnum.NEW, 0, {
    isDefault: true,
  }),
  t('under-review', 'Under Review', '#0EA5E9', TicketStatusTypeEnum.TRIAGED, 1),
  t('accepted', 'Accepted', '#6366F1', TicketStatusTypeEnum.TRIAGED, 2),
  t('planned', 'Planned', '#8B5CF6', TicketStatusTypeEnum.IN_PROGRESS, 3),
  t(
    'in-progress',
    'In Progress',
    '#2563EB',
    TicketStatusTypeEnum.IN_PROGRESS,
    4,
  ),
  t('shipped', 'Shipped', '#10B981', TicketStatusTypeEnum.RESOLVED, 5),
  t('declined', 'Declined', '#EF4444', TicketStatusTypeEnum.CLOSED, 6),
]

const BUG_STAGES: CrmStageConfig[] = [
  t('new', 'New', '#64748B', TicketStatusTypeEnum.NEW, 0, { isDefault: true }),
  t('triaged', 'Triaged', '#0EA5E9', TicketStatusTypeEnum.TRIAGED, 1),
  t(
    'in-progress',
    'In Progress',
    '#2563EB',
    TicketStatusTypeEnum.IN_PROGRESS,
    2,
  ),
  t(
    'in-review',
    'In Review',
    '#A855F7',
    TicketStatusTypeEnum.WAITING_ON_INTERNAL,
    3,
  ),
  t('fixed', 'Fixed', '#10B981', TicketStatusTypeEnum.RESOLVED, 4),
  t('wont-fix', "Won't Fix", '#334155', TicketStatusTypeEnum.CLOSED, 5),
]

const APPROVAL_STAGES: CrmStageConfig[] = [
  t('submitted', 'Submitted', '#64748B', TicketStatusTypeEnum.NEW, 0, {
    isDefault: true,
  }),
  t('in-review', 'In Review', '#0EA5E9', TicketStatusTypeEnum.IN_PROGRESS, 1),
  t('approved', 'Approved', '#10B981', TicketStatusTypeEnum.RESOLVED, 2),
  t('denied', 'Denied', '#EF4444', TicketStatusTypeEnum.CLOSED, 3),
]

const CONTRACT_REVIEW_STAGES: CrmStageConfig[] = [
  t('requested', 'Requested', '#64748B', TicketStatusTypeEnum.NEW, 0, {
    isDefault: true,
  }),
  t('in-review', 'In Review', '#0EA5E9', TicketStatusTypeEnum.IN_PROGRESS, 1),
  t(
    'negotiating',
    'Negotiating',
    '#F59E0B',
    TicketStatusTypeEnum.WAITING_ON_REQUESTER,
    2,
  ),
  t('signed', 'Signed', '#10B981', TicketStatusTypeEnum.RESOLVED, 3),
  t('rejected', 'Rejected', '#EF4444', TicketStatusTypeEnum.CLOSED, 4),
]

/* ----- The catalog -------------------------------------------------------- */

export const TICKET_TYPE_TEMPLATES: TicketTypeTemplate[] = [
  // --- Product & Engineering ---
  {
    key: 'feature_request',
    name: 'Feature Request',
    pluralName: 'Feature Requests',
    description:
      'Customer and internal feature ideas, ranked by who asked and revenue behind them.',
    icon: 'Sparkles',
    color: '#8B5CF6',
    bundle: TicketTypeBundle.PRODUCT_ENG,
    capabilities: {
      allowsMultiRequester: true,
      allowsPromoteToTask: true,
      allowsPromoteToProject: true,
      allowsPublicView: true,
      weightedAggregation: true,
    },
    stages: FEATURE_REQUEST_STAGES,
    defaultStageId: 'submitted',
    seededForTech: true,
  },
  {
    key: 'bug',
    name: 'Bug',
    pluralName: 'Bugs',
    description: 'Defects and regressions reported by customers or the team.',
    icon: 'Bug',
    color: '#EF4444',
    bundle: TicketTypeBundle.PRODUCT_ENG,
    capabilities: { allowsMultiRequester: true, allowsPromoteToTask: true },
    stages: BUG_STAGES,
    defaultStageId: 'new',
    seededForTech: true,
  },
  {
    key: 'tech_debt',
    name: 'Tech Debt',
    pluralName: 'Tech Debt',
    description: 'Internal engineering improvements and cleanup.',
    icon: 'Wrench',
    color: '#6B7280',
    bundle: TicketTypeBundle.PRODUCT_ENG,
    capabilities: { allowsPromoteToTask: true },
    stages: BUG_STAGES,
    defaultStageId: 'new',
  },
  {
    key: 'incident',
    name: 'Incident',
    pluralName: 'Incidents',
    description: 'Production incidents needing rapid response.',
    icon: 'Siren',
    color: '#DC2626',
    bundle: TicketTypeBundle.PRODUCT_ENG,
    capabilities: { allowsPromoteToTask: true, hasSlaTimer: true },
    stages: SUPPORT_STAGES,
    defaultStageId: 'new',
  },

  // --- Customer Support ---
  {
    key: 'support',
    name: 'Support Ticket',
    pluralName: 'Support Tickets',
    description: 'Customer support requests from email, forms, or the team.',
    icon: 'LifeBuoy',
    color: '#0EA5E9',
    bundle: TicketTypeBundle.SUPPORT,
    capabilities: {
      allowsPromoteToTask: true,
      allowsPublicSubmit: true,
      hasSlaTimer: true,
    },
    stages: SUPPORT_STAGES,
    defaultStageId: 'new',
    seededByDefault: true,
  },
  {
    key: 'escalation',
    name: 'Escalation',
    pluralName: 'Escalations',
    description: 'High-priority support issues escalated to specialists.',
    icon: 'TrendingUp',
    color: '#F97316',
    bundle: TicketTypeBundle.SUPPORT,
    capabilities: { allowsPromoteToTask: true, hasSlaTimer: true },
    stages: SUPPORT_STAGES,
    defaultStageId: 'new',
  },
  {
    key: 'refund_request',
    name: 'Refund Request',
    pluralName: 'Refund Requests',
    description: 'Customer refund and billing adjustment requests.',
    icon: 'BadgeDollarSign',
    color: '#14B8A6',
    bundle: TicketTypeBundle.SUPPORT,
    capabilities: { requiresApproval: true },
    stages: APPROVAL_STAGES,
    defaultStageId: 'submitted',
  },

  // --- Sales & Success ---
  {
    key: 'deal_question',
    name: 'Deal Question',
    pluralName: 'Deal Questions',
    description: 'Questions from sales tied to an open deal.',
    icon: 'HandCoins',
    color: '#22C55E',
    bundle: TicketTypeBundle.SALES,
    capabilities: { allowsPromoteToTask: true },
    stages: SUPPORT_STAGES,
    defaultStageId: 'new',
  },
  {
    key: 'renewal_risk',
    name: 'Renewal Risk',
    pluralName: 'Renewal Risks',
    description: 'At-risk accounts flagged for retention work.',
    icon: 'ShieldAlert',
    color: '#F59E0B',
    bundle: TicketTypeBundle.SALES,
    capabilities: { allowsPromoteToTask: true, weightedAggregation: true },
    stages: SUPPORT_STAGES,
    defaultStageId: 'new',
  },

  // --- Legal ---
  {
    key: 'contract_review',
    name: 'Contract Review',
    pluralName: 'Contract Reviews',
    description: 'Legal review of contracts and agreements.',
    icon: 'FileSignature',
    color: '#7C3AED',
    bundle: TicketTypeBundle.LEGAL,
    capabilities: {
      requiresApproval: true,
      allowsPromoteToProject: true,
      privateByDefault: true,
    },
    stages: CONTRACT_REVIEW_STAGES,
    defaultStageId: 'requested',
    requiredFields: ['requesterEntityId', 'dueDate'],
    visibilityScope: TicketTypeVisibilityScope.RESTRICTED,
  },
  {
    key: 'nda_request',
    name: 'NDA Request',
    pluralName: 'NDA Requests',
    description: 'Requests to prepare or review non-disclosure agreements.',
    icon: 'FileLock',
    color: '#6D28D9',
    bundle: TicketTypeBundle.LEGAL,
    capabilities: { requiresApproval: true, privateByDefault: true },
    stages: APPROVAL_STAGES,
    defaultStageId: 'submitted',
    visibilityScope: TicketTypeVisibilityScope.RESTRICTED,
  },

  // --- HR & People ---
  {
    key: 'time_off',
    name: 'Time-Off Request',
    pluralName: 'Time-Off Requests',
    description: 'Employee time-off requests routed for approval.',
    icon: 'Palmtree',
    color: '#0D9488',
    bundle: TicketTypeBundle.HR,
    capabilities: { requiresApproval: true, privateByDefault: true },
    stages: APPROVAL_STAGES,
    defaultStageId: 'submitted',
    requiredFields: ['requesterUserId', 'dueDate'],
    visibilityScope: TicketTypeVisibilityScope.RESTRICTED,
  },
  {
    key: 'employee_question',
    name: 'Employee Question',
    pluralName: 'Employee Questions',
    description: 'Confidential questions for the People team.',
    icon: 'MessageCircleQuestion',
    color: '#0891B2',
    bundle: TicketTypeBundle.HR,
    capabilities: { privateByDefault: true },
    stages: SUPPORT_STAGES,
    defaultStageId: 'new',
    visibilityScope: TicketTypeVisibilityScope.RESTRICTED,
  },

  // --- IT & Ops ---
  {
    key: 'it_help',
    name: 'IT Help Desk',
    pluralName: 'IT Help Desk',
    description: 'Internal IT support requests.',
    icon: 'Laptop',
    color: '#3B82F6',
    bundle: TicketTypeBundle.IT,
    capabilities: {
      allowsPromoteToTask: true,
      allowsPublicSubmit: true,
      hasSlaTimer: true,
    },
    stages: SUPPORT_STAGES,
    defaultStageId: 'new',
  },
  {
    key: 'access_request',
    name: 'Access Request',
    pluralName: 'Access Requests',
    description: 'Requests for system or facility access, routed for approval.',
    icon: 'KeyRound',
    color: '#2563EB',
    bundle: TicketTypeBundle.IT,
    capabilities: { requiresApproval: true },
    stages: APPROVAL_STAGES,
    defaultStageId: 'submitted',
  },

  // --- Finance ---
  {
    key: 'po_request',
    name: 'PO Request',
    pluralName: 'PO Requests',
    description: 'Purchase order requests routed for approval.',
    icon: 'Receipt',
    color: '#059669',
    bundle: TicketTypeBundle.FINANCE,
    capabilities: { requiresApproval: true },
    stages: APPROVAL_STAGES,
    defaultStageId: 'submitted',
  },

  // --- General ---
  {
    key: 'idea',
    name: 'Idea',
    pluralName: 'Ideas',
    description: 'A lightweight catch-all for ideas and suggestions.',
    icon: 'Lightbulb',
    color: '#EAB308',
    bundle: TicketTypeBundle.GENERIC,
    capabilities: { allowsMultiRequester: true, allowsPromoteToTask: true },
    stages: FEATURE_REQUEST_STAGES,
    defaultStageId: 'submitted',
  },
  {
    key: 'question',
    name: 'Question',
    pluralName: 'Questions',
    description: 'General questions for the team.',
    icon: 'HelpCircle',
    color: '#64748B',
    bundle: TicketTypeBundle.GENERIC,
    capabilities: {},
    stages: SUPPORT_STAGES,
    defaultStageId: 'new',
    seededByDefault: true,
  },
]

/* -------------------------------------------------------------------------- */
/*  Helpers                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Universal types installed automatically for every new organization,
 * regardless of industry (e.g. Support Ticket, Question).
 */
export const DEFAULT_SEEDED_TICKET_TYPE_KEYS: string[] =
  TICKET_TYPE_TEMPLATES.filter(tpl => tpl.seededByDefault).map(tpl => tpl.key)

/**
 * Product-led types installed automatically only for software/technology orgs,
 * on top of the universal set (e.g. Feature Request, Bug). Any org can still
 * install these later from the template catalog.
 */
export const TECH_SEEDED_TICKET_TYPE_KEYS: string[] =
  TICKET_TYPE_TEMPLATES.filter(tpl => tpl.seededForTech).map(tpl => tpl.key)

/**
 * Org `type` values (see OrganizationTypes) that should receive the
 * software/technology default ticket types at creation.
 */
export const SOFTWARE_ORG_TYPES: string[] = ['Technology']

/** Whether an org's industry/type marks it as a software/technology company. */
export function isSoftwareOrgType(orgType?: string | null): boolean {
  return orgType != null && SOFTWARE_ORG_TYPES.includes(orgType)
}

/**
 * The ticket-type keys to seed for a brand-new org given its industry/type.
 * Tech orgs get the universal set plus the product-led set; everyone else gets
 * just the universal set.
 */
export function getDefaultSeededTicketTypeKeys(
  orgType?: string | null,
): string[] {
  return isSoftwareOrgType(orgType)
    ? [...DEFAULT_SEEDED_TICKET_TYPE_KEYS, ...TECH_SEEDED_TICKET_TYPE_KEYS]
    : [...DEFAULT_SEEDED_TICKET_TYPE_KEYS]
}

export function getTicketTypeTemplate(
  key: string,
): TicketTypeTemplate | undefined {
  return TICKET_TYPE_TEMPLATES.find(tpl => tpl.key === key)
}

export function getTicketTypeTemplatesByBundle(
  bundle: TicketTypeBundle,
): TicketTypeTemplate[] {
  return TICKET_TYPE_TEMPLATES.filter(tpl => tpl.bundle === bundle)
}

/** Merge a template's partial capabilities onto the safe baseline. */
export function resolveTicketCapabilities(
  partial?: Partial<TicketCapabilities>,
): TicketCapabilities {
  return { ...DEFAULT_TICKET_CAPABILITIES, ...(partial ?? {}) }
}

/** Whether a ticket type exposes a given capability. */
export function ticketTypeCan(
  typeDef: Pick<TicketTypeDef, TicketCapabilityKey> | null | undefined,
  capability: TicketCapabilityKey,
): boolean {
  return typeDef?.[capability] === true
}
